import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import { api, type CrmSection, type StaffUser } from "@/lib/api";

const sections: Array<{ id: CrmSection; label: string }> = [
  { id: "catalog", label: "Каталог" },
  { id: "orders", label: "Заказы" },
  { id: "requests", label: "Заявки" },
  { id: "analytics", label: "Аналитика" },
  { id: "users", label: "Пользователи" },
];

type UserForm = {
  login: string;
  email: string;
  password: string;
  role: "admin" | "manager";
  accessSections: CrmSection[];
  canManageUsers: boolean;
  isActive: boolean;
};

const emptyForm = (): UserForm => ({
  login: "",
  email: "",
  password: "",
  role: "manager",
  accessSections: ["catalog", "orders"],
  canManageUsers: false,
  isActive: true,
});

function roleLabel(role: "admin" | "manager") {
  return role === "admin" ? "Администратор" : "Менеджер";
}

function formFromUser(user: StaffUser): UserForm {
  return {
    login: user.login,
    email: user.email || "",
    password: "",
    role: user.role,
    accessSections: user.access_sections,
    canManageUsers: user.can_manage_users,
    isActive: user.is_active,
  };
}

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const selectedUser = useMemo(() => users.find((item) => item.id === selectedUserId) || null, [selectedUserId, users]);
  const canManageUsers = !!user?.canManageUsers;

  useEffect(() => {
    let mounted = true;
    if (!canManageUsers) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .users()
      .then((nextUsers) => {
        if (!mounted) return;
        setUsers(nextUsers);
        setSelectedUserId(nextUsers[0]?.id || null);
        setForm(nextUsers[0] ? formFromUser(nextUsers[0]) : emptyForm());
        setError("");
      })
      .catch((err) => mounted && setError(err instanceof Error ? err.message : "Не удалось загрузить пользователей"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [canManageUsers]);

  const startCreate = () => {
    setSelectedUserId(null);
    setForm(emptyForm());
    setError("");
    setNotice("");
  };

  const selectUser = (nextUser: StaffUser) => {
    setSelectedUserId(nextUser.id);
    setForm(formFromUser(nextUser));
    setError("");
    setNotice("");
  };

  const toggleSection = (section: CrmSection) => {
    setForm((prev) => ({
      ...prev,
      accessSections: prev.accessSections.includes(section)
        ? prev.accessSections.filter((item) => item !== section)
        : [...prev.accessSections, section],
    }));
  };

  const updateRole = (role: "admin" | "manager") => {
    setForm((prev) => ({
      ...prev,
      role,
      accessSections:
        role === "admin" && prev.accessSections.length === 0 ? sections.map((section) => section.id) : prev.accessSections,
    }));
  };

  const saveUser = async () => {
    if (!form.login.trim()) {
      setError("Укажите логин");
      return;
    }
    if (!selectedUser && form.password.length < 8) {
      setError("Для нового пользователя укажите пароль от 8 символов");
      return;
    }
    if (form.accessSections.length === 0) {
      setError("Выберите хотя бы один раздел CRM");
      return;
    }

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        email: form.email.trim() || null,
        role: form.role,
        access_sections: form.accessSections,
        can_manage_users: form.canManageUsers,
        is_active: form.isActive,
      };
      const saved = selectedUser
        ? await api.updateUser(selectedUser.id, {
            ...payload,
            ...(form.password ? { password: form.password } : {}),
          })
        : await api.createUser({
            ...payload,
            login: form.login.trim(),
            password: form.password,
          });
      setUsers((prev) => [saved, ...prev.filter((item) => item.id !== saved.id)].sort((a, b) => a.login.localeCompare(b.login, "ru")));
      setSelectedUserId(saved.id);
      setForm(formFromUser(saved));
      setNotice("Пользователь сохранён");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сохранить пользователя");
    } finally {
      setSaving(false);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-[-0.03em] text-ink">Пользователи CRM</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-ink-4">У вашей учетной записи нет права управлять пользователями.</p>
        </div>
        <div className="panel p-6">
          <p className="text-sm text-ink-4">Обратитесь к супер-администратору `voldan`, чтобы получить доступ.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-[-0.03em] text-ink">Пользователи CRM</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-ink-4">
            Добавляйте пользователей, назначайте роли, разделы CRM и право управлять другими пользователями.
          </p>
        </div>
        <button type="button" onClick={startCreate} className="btn-primary">
          <i className="ri-user-add-line" />
          Добавить пользователя
        </button>
      </div>

      {error ? <div className="rounded-xl border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger">{error}</div> : null}
      {notice ? <div className="rounded-xl border border-success-border bg-success-bg px-4 py-3 text-sm text-success">{notice}</div> : null}

      <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="panel overflow-hidden">
          <div className="border-b border-border-soft px-5 py-4">
            <h3 className="text-base font-semibold text-ink">Список пользователей</h3>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-ink-4">Загрузка...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-border-soft bg-panel-2 text-xs uppercase tracking-[0.12em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Пользователь</th>
                    <th className="px-5 py-3 font-semibold">Роль</th>
                    <th className="px-5 py-3 font-semibold">Разделы</th>
                    <th className="px-5 py-3 font-semibold">Права</th>
                    <th className="px-5 py-3 font-semibold">Статус</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-soft">
                  {users.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => selectUser(item)}
                      className={`cursor-pointer transition hover:bg-panel-2 ${selectedUserId === item.id ? "bg-amber-50" : ""}`}
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-ink">{item.login}</p>
                        <p className="mt-1 text-xs text-ink-4">{item.email || "email не указан"}</p>
                      </td>
                      <td className="px-5 py-4 text-ink-3">{roleLabel(item.role)}</td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {item.access_sections.map((section) => (
                            <span key={section} className="status-badge border-info-border bg-info-bg text-info">
                              {sections.find((entry) => entry.id === section)?.label || section}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-ink-3">{item.can_manage_users ? "Может добавлять пользователей" : "Без управления"}</td>
                      <td className="px-5 py-4">
                        <span className={`status-badge ${item.is_active ? "border-success-border bg-success-bg text-success" : "border-danger-border bg-danger-bg text-danger"}`}>
                          {item.is_active ? "Активен" : "Отключен"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="panel p-5">
          <h3 className="text-base font-semibold text-ink">{selectedUser ? "Редактирование пользователя" : "Новый пользователь"}</h3>
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-ink-mute">Логин</label>
              <input
                value={form.login}
                onChange={(event) => setForm((prev) => ({ ...prev, login: event.target.value }))}
                disabled={!!selectedUser}
                className="w-full rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink disabled:bg-panel-2 disabled:text-ink-4"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-ink-mute">Email</label>
              <input
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                className="w-full rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-ink-mute">
                {selectedUser ? "Новый пароль" : "Пароль"}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                className="w-full rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink"
                placeholder={selectedUser ? "Оставьте пустым, чтобы не менять" : "Минимум 8 символов"}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-ink-mute">Роль</label>
              <select
                value={form.role}
                onChange={(event) => updateRole(event.target.value as "admin" | "manager")}
                disabled={selectedUser?.login === "voldan"}
                className="w-full rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink disabled:bg-panel-2 disabled:text-ink-4"
              >
                <option value="manager">Менеджер</option>
                <option value="admin">Администратор</option>
              </select>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-mute">Разделы CRM</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {sections.map((section) => (
                  <label key={section.id} className="flex items-center gap-2 rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink-3">
                    <input
                      type="checkbox"
                      checked={form.accessSections.includes(section.id)}
                      onChange={() => toggleSection(section.id)}
                      disabled={selectedUser?.login === "voldan"}
                      className="h-4 w-4 rounded border-border text-amber focus:ring-amber"
                    />
                    {section.label}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink-3">
              <input
                type="checkbox"
                checked={form.canManageUsers}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    canManageUsers: event.target.checked,
                    accessSections:
                      event.target.checked && !prev.accessSections.includes("users")
                        ? [...prev.accessSections, "users"]
                        : prev.accessSections,
                  }))
                }
                disabled={selectedUser?.login === "voldan"}
                className="h-4 w-4 rounded border-border text-amber focus:ring-amber"
              />
              Может добавлять и редактировать пользователей
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-border bg-panel px-3 py-2 text-sm text-ink-3">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={selectedUser?.login === "voldan"}
                className="h-4 w-4 rounded border-border text-amber focus:ring-amber"
              />
              Пользователь активен
            </label>
            <button type="button" onClick={saveUser} disabled={saving} className="btn-primary w-full justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
